import type { Request } from 'express';
import { VERSION } from '~/modules/config';
import { CORRELATION_ID_PARAM, isClient, logger } from '~/modules/utilities/cross_env_utils';
import { getActiveExperiments, getExperimentsFromRequest } from '~/modules/utilities/experimentationUtils';

export const ERROR_CODES = {
    GENERIC_ISSUE: 'GENERIC_ISSUE',
    PAGE_NOT_FOUND: 'PAGE_NOT_FOUND',
    MISSING_ROUTE_PARAM: 'MISSING_ROUTE_PARAM',
    MISSING_QUERY_PARAM: 'MISSING_QUERY_PARAM',
    INVALID_QUERY_PARAM: 'INVALID_QUERY_PARAM',
    INVALID_STATE: 'INVALID_STATE',
    INVALID_LISTING: 'INVALID_LISTING',
    MISSING_LISTING: 'MISSING_LISTING',
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
};

/** Experiment payload from getExperimentsFromRequest (header preferred, else `optimizely-experiments` cookie). */
export function extractExperimentFromRequest(req: Partial<Request>) {
    return { experiments: getExperimentsFromRequest(req) };
}

/**
 * Options for handleException
 * @param debugData - Optional additional debug information for logging
 * @param req - When provided (typically server-side), used to attach `experiments`, the request `path`, and the `x-correlation-id` header as `correlationId`; the request object itself is not merged into debug data
 */
export interface HandleExceptionOptions {
    debugData?: AnyObject;
    req?: Partial<Request>;
    level?: 'error' | 'warn';
}

/**
 * IMPORTANT: Be careful not to send any PII through any of these parameters.
 *
 * Server: experiments in logs come from this HTTP request header or cookie (see getExperimentsFromRequest).
 * Client: experiments come from getActiveExperiments() (Optimizely on window).
 * @param exception - The error/exception to log
 * @param context - Context string describing where the error occurred
 * @param options - Optional object containing debugData and req
 */
export function handleException(exception: unknown, context: string | AnyObject, options?: HandleExceptionOptions) {
    const formattedContext = typeof context === 'object' ? formatContext(context) : context;
    let debugData = options?.debugData;

    if (options?.req) {
        const experiments = extractExperimentFromRequest(options.req);
        const path = options.req.originalUrl ?? options.req.path;
        const correlationId = options.req.headers?.[CORRELATION_ID_PARAM] as string | undefined;
        debugData = {
            ...debugData,
            ...experiments,
            ...(path ? { path } : {}),
            ...(correlationId ? { correlationId } : {}),
        };
    }

    const level = options?.level ?? 'error';

    // Client-Side Logging
    if (isClient) {
        logClientException(exception, formattedContext, debugData, level);
    }
    // Server-Side Logging
    else {
        // Check if logger from cross_env_utils has a custom handleServerException function
        if (typeof logger.handleServerException === 'function') {
            // Call the custom server-side exception handler
            logger.handleServerException(exception, formattedContext, debugData, level);
        } else {
            // Fallback logic if custom server-side handler is not set up
            console.error("Custom 'handleServerException' not found. Falling back to default server logging.");
            logClientException(exception, formattedContext, debugData, level);
        }
    }
}

export interface ExceptionPayload {
    origin: 'client' | 'server';
    version: string;
    context: string;
    path?: string;
    name: string;
    message: string;
    status: number;
    experiments: unknown[];
    correlationId?: string;
    code?: string;
    stack?: string;
    [key: string]: unknown;
}

/**
 * Assembles the common ExceptionPayload shape shared by the client and server loggers so the two
 * schemas cannot drift. Origin-specific logic (message formatting, status extraction) stays with the
 * caller and is passed in as `message`/`status`; everything else — origin, version, context, name,
 * flattened debugData, experiments (active Optimizely experiments preferred over caller-supplied
 * `debugData.experiments`), path, correlationId, code, stack — is built here.
 */
export function buildExceptionPayload(
    exception: unknown,
    args: {
        origin: 'client' | 'server';
        context: string;
        message: string;
        status: number;
        debugData?: AnyObject;
        path?: string;
        correlationId?: string;
    },
): ExceptionPayload {
    const safe = safeDebugData(args.debugData);
    const activeExperiments = getActiveExperiments();
    const exceptionCode = (exception as Partial<{ code: unknown }>)?.code;

    // Cap the client-controllable correlationId (from the request header on the server or the axios
    // error headers on the client) so one request can't bloat its Splunk record past a normal id,
    // mirroring the 10KB cap on the Optimizely-experiments cookie. Applied here so both origins are
    // covered at the single point where correlationId resolves.
    const rawCorrelationId = (args.correlationId ?? (args.debugData?.correlationId as string) ?? undefined) as
        | string
        | undefined;
    const correlationId = typeof rawCorrelationId === 'string' ? rawCorrelationId.slice(0, 128) : rawCorrelationId;

    const payload: ExceptionPayload = {
        ...safe,
        origin: args.origin,
        version: VERSION,
        context: args.context,
        path: args.path ?? (safe.path as string | undefined),
        correlationId,
        name: exception instanceof Error ? exception.name : 'Unknown',
        message: args.message,
        code: typeof exceptionCode === 'string' ? exceptionCode : undefined,
        status: args.status,
        experiments:
            activeExperiments.length > 0 ? activeExperiments : ((args.debugData?.experiments as unknown[]) ?? []),
    };

    if (exception instanceof Error && exception.stack) {
        payload.stack = exception.stack;
    }

    return payload;
}

/**
 * Stringifies an exception payload for emission, falling back to a plain summary string if the
 * payload can't be serialized (e.g. a circular value) so the logging path itself never throws.
 * Shared by the client and server loggers so both degrade identically.
 */
export function safeStringifyPayload(payload: ExceptionPayload): string {
    try {
        return JSON.stringify(payload);
    } catch {
        return `[${payload.context}] Error payload could not be serialized: ${payload.name} - ${payload.message}`;
    }
}

// Non-primitive values are intentionally JSON.stringified (not flattened) to keep the payload flat while remaining searchable in Splunk.
function safeDebugData(debugData?: AnyObject): Record<string, string | number | boolean> {
    if (!debugData) {
        return {};
    }
    const safe: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(debugData)) {
        // Drop upstream response bodies outright — callers (http.ts, listing_service.ts) pass
        // `response: error.response.data`, which can carry PII. #2465 removed the dedicated field;
        // this keeps it from sneaking back in via the debugData spread.
        if (key === 'response' || value === undefined || value === null) {
            continue;
        }
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            safe[key] = value;
        } else {
            try {
                safe[key] = JSON.stringify(value);
            } catch {
                safe[key] = '[unserializable]';
            }
        }
    }
    return safe;
}

// Client-side exception logging logic
function logClientException(
    exception: unknown,
    context: string,
    debugData: AnyObject | undefined,
    level: 'error' | 'warn',
) {
    const dangerousUntypedError = exception as Partial<{
        code: unknown;
        message: unknown;
        config: Partial<{ headers: Record<string, unknown> }>;
        response: Partial<{ status: number; data: unknown }>;
    }>;

    const headerCorrelationId = dangerousUntypedError?.config?.headers?.[CORRELATION_ID_PARAM] as string | undefined;

    const errorPayload = buildExceptionPayload(exception, {
        origin: 'client',
        context,
        message:
            exception instanceof Error
                ? exception.message
                : typeof dangerousUntypedError?.message === 'string'
                  ? dangerousUntypedError.message
                  : String(exception),
        status: dangerousUntypedError?.response?.status ?? -1,
        debugData,
        path: getClientPath(),
        correlationId: headerCorrelationId,
    });

    logger[level](safeStringifyPayload(errorPayload));
}

function getClientPath(): string | undefined {
    try {
        return (globalThis as unknown as { window?: { location?: { pathname?: string } } }).window?.location?.pathname;
    } catch {
        return undefined;
    }
}

// Formats the context object into a readable string
export function formatContext(contextObj: AnyObject): string {
    return `(${Object.entries(contextObj)
        .map(([key, value]) => {
            // Properly serialize complex objects and arrays
            if (typeof value === 'object' && value !== null) {
                try {
                    return `${key}=${JSON.stringify(value)}`;
                } catch {
                    return `${key}=[unserializable]`;
                }
            }
            return `${key}=${value}`;
        })
        .join(', ')})`;
}
